import React, { useState, useEffect, useRef } from 'react'
import { initSentry, wrapWithSentry, routingInstrumentation, setSentryUser } from './src/lib/sentry'

// Initialize Sentry before anything else (no-op if EXPO_PUBLIC_SENTRY_DSN not set)
initSentry()

import { StatusBar } from 'expo-status-bar'
import { NavigationContainer, NavigationContainerRef } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { AuthProvider, useAuth } from './src/contexts/AuthContext'
import { I18nProvider, useLanguage } from '@fliisi/i18n'
import { ToastProvider } from './src/components/Toast'
import { NavShellProvider, useNavShell } from './src/contexts/NavShellContext'
import NavigationShell from './src/components/NavigationShell'
import { TamaguiProvider, config } from '@fliisi/pollen'
import { RootStackParamList } from './src/types/navigation'
import { ActivityIndicator, View, Platform, StyleSheet, useWindowDimensions } from 'react-native'
import * as Linking from 'expo-linking'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { supabase } from './src/lib/supabase'
import { useFonts } from 'expo-font'
import { PlayfairDisplay_700Bold_Italic } from '@expo-google-fonts/playfair-display'

// Note: Stripe native provider is disabled for web bundling compatibility
// Native payments will be enabled in separate native builds
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''

// Screens
import LandingScreen from './src/screens/LandingScreen'
import LoginScreen from './src/screens/LoginScreen'

import HomeScreen from './src/screens/HomeScreen'
import CameraScreen from './src/screens/CameraScreen'
import ProductDetailScreen from './src/screens/ProductDetailScreen'
import ProductViewScreen from './src/screens/ProductViewScreen'
import JoinMarketScreen from './src/screens/JoinMarketScreen'
import CreateMarketScreen from './src/screens/CreateMarketScreen'
import MarketAdminScreen from './src/screens/MarketAdminScreen'
import MarketSellersScreen from './src/screens/MarketSellersScreen'
import MarketSpacesScreen from './src/screens/MarketSpacesScreen'
import MarketSettingsScreen from './src/screens/MarketSettingsScreen'
import MarketPayoutsScreen from './src/screens/MarketPayoutsScreen'
import MarketPOSScreen from './src/screens/MarketPOSScreen'
import MarketPrintTagsScreen from './src/screens/MarketPrintTagsScreen'
import MarketRefundsScreen from './src/screens/MarketRefundsScreen'
import SpaceReservationsScreen from './src/screens/SpaceReservationsScreen'
import SpaceBrowseScreen from './src/screens/SpaceBrowseScreen'
import FloorPlanEditorScreen from './src/screens/FloorPlanEditorScreen'
import VisualSpaceBrowseScreen from './src/screens/VisualSpaceBrowseScreen'
import BulkUploadScreen from './src/screens/BulkUploadScreen'
import ReservationSettingsScreen from './src/screens/ReservationSettingsScreen'
import PrintTagsScreen from './src/screens/PrintTagsScreen'
import StorePrintTagsScreen from './src/screens/StorePrintTagsScreen'
import CheckoutScreen from './src/screens/CheckoutScreen'
// Store screens
import MyStoresScreen from './src/screens/MyStoresScreen'
import StoreInventoryScreen from './src/screens/StoreInventoryScreen'
import CreateStoreScreen from './src/screens/CreateStoreScreen'
import StoreCameraScreen from './src/screens/StoreCameraScreen'
import StoreSettingsScreen from './src/screens/StoreSettingsScreen'
import SupplierManagementScreen from './src/screens/SupplierManagementScreen'
import InventoryReportsScreen from './src/screens/InventoryReportsScreen'
import StoreTransactionsScreen from './src/screens/StoreTransactionsScreen'
import BoothViewScreen from './src/screens/BoothViewScreen'
// Profile & Account screens
import DraftProductsScreen from './src/screens/DraftProductsScreen'
import FavoritesScreen from './src/screens/FavoritesScreen'
import PurchaseHistoryScreen from './src/screens/PurchaseHistoryScreen'
import ProfileSettingsScreen from './src/screens/ProfileSettingsScreen'
// Merchant screens
import MerchantStoreSelectScreen from './src/screens/MerchantStoreSelectScreen'
import MerchantDashboardScreen from './src/screens/MerchantDashboardScreen'
import MerchantPOSScreen from './src/screens/MerchantPOSScreen'
import MerchantActivityScreen from './src/screens/MerchantActivityScreen'
import MerchantMoreScreen from './src/screens/MerchantMoreScreen'
import MerchantStaffScreen from './src/screens/MerchantStaffScreen'
import SocialAccountsScreen from './src/screens/SocialAccountsScreen'
// Inventory CMS screens
import CategoryManagementScreen from './src/screens/CategoryManagementScreen'
import StockAdjustmentScreen from './src/screens/StockAdjustmentScreen'
import PurchaseOrdersScreen from './src/screens/PurchaseOrdersScreen'
import PurchaseOrderDetailScreen from './src/screens/PurchaseOrderDetailScreen'
import MerchantProductEditScreen from './src/screens/MerchantProductEditScreen'
// Map screens
import MapBrowseScreen from './src/screens/MapBrowseScreen'
import StoreBrowseScreen from './src/screens/StoreBrowseScreen'
// Buyer screens
import BuyerHomeScreen from './src/screens/BuyerHomeScreen'
import DiscoveriesScreen from './src/screens/DiscoveriesScreen'
import SearchScreen from './src/screens/SearchScreen'
import BuyerProfileScreen from './src/screens/BuyerProfileScreen'
// Offer screens
import SubmitOfferScreen from './src/screens/SubmitOfferScreen'
import MyOffersScreen from './src/screens/MyOffersScreen'
import IncomingOffersScreen from './src/screens/IncomingOffersScreen'
import UnsubscribeScreen from './src/screens/UnsubscribeScreen'

const Stack = createNativeStackNavigator<RootStackParamList>()

// Navigation ref for programmatic navigation
const navigationRef = React.createRef<NavigationContainerRef<RootStackParamList>>()

function AppNavigator() {
  const { session, loading } = useAuth()
  const { t } = useLanguage()
  const { width } = useWindowDimensions()
  const isDesktop = width > 768
  const [loginMode, setLoginMode] = useState<'seller' | 'merchant' | null>(null)
  const [modeLoading, setModeLoading] = useState(true)
  const [hasNavigated, setHasNavigated] = useState(false)

  // Load login mode when session changes
  useEffect(() => {
    const loadLoginMode = async () => {
      if (session) {
        const mode = await AsyncStorage.getItem('loginMode')
        setLoginMode(mode === 'merchant' ? 'merchant' : 'seller')
      } else {
        setLoginMode(null)
        setHasNavigated(false) // Reset on logout
        // Only navigate to Landing after logout if we're on a protected route
        // Don't override deep links to public routes like BoothView, ProductView, etc.
        setTimeout(() => {
          const currentRoute = navigationRef.current?.getCurrentRoute()?.name
          const publicRoutes = ['Landing', 'BuyerHome', 'SearchScreen', 'BuyerProfile', 'ProductView', 'BoothView', 'Login', 'Unsubscribe']
          if (currentRoute && !publicRoutes.includes(currentRoute)) {
            navigationRef.current?.reset({
              index: 0,
              routes: [{ name: 'Landing' }],
            })
          }
        }, 100)
      }
      setModeLoading(false)
    }
    loadLoginMode()
  }, [session])

  // Navigate to merchant screen after OAuth login
  useEffect(() => {
    if (session && loginMode === 'merchant' && !hasNavigated && !modeLoading) {
      setHasNavigated(true)
      // Use setTimeout to ensure navigation is ready
      setTimeout(() => {
        // Only redirect to store select if we're at the root path (fresh login, not a page refresh on a deep link)
        const pathname = typeof window !== 'undefined' ? window.location?.pathname : '/'
        if (!pathname || pathname === '/') {
          navigationRef.current?.reset({
            index: 0,
            routes: [{ name: 'MerchantStoreSelect' }],
          })
        }
      }, 100)
    }
  }, [session, loginMode, hasNavigated, modeLoading])

  if (loading || modeLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color="#4A90A4" />
      </View>
    )
  }

  const screenOptions = {
    headerShown: false,
    gestureEnabled: Platform.OS !== 'web',
    animation: Platform.OS === 'web' ? 'none' as const : 'default' as const,
  }

  // Determine initial route based on login mode
  const isMerchantMode = session && loginMode === 'merchant'

  // Unsigned users land on Landing page
  // After merchant login, redirect to MerchantStoreSelect
  const getInitialRoute = () => {
    if (session && isMerchantMode) {
      return 'MerchantStoreSelect'
    }
    if (session) {
      return 'BuyerHome'
    }
    return 'Landing'
  }

  return (
    <View style={{ flex: 1 }}>
      <NavigationShell>
      <Stack.Navigator
        screenOptions={screenOptions}
        initialRouteName={getInitialRoute()}
      >
        {/* Public screens - always available */}
        <Stack.Screen name="Unsubscribe" component={UnsubscribeScreen} />
        <Stack.Screen name="Landing" component={LandingScreen} />
        <Stack.Screen name="BuyerHome" component={BuyerHomeScreen} />
        <Stack.Screen name="SearchScreen" component={SearchScreen} />
        <Stack.Screen name="BuyerProfile" component={BuyerProfileScreen} />
        <Stack.Screen name="Discoveries" component={DiscoveriesScreen} />
        <Stack.Screen name="MapBrowse" component={MapBrowseScreen} />
        <Stack.Screen name="StoreBrowse" component={StoreBrowseScreen} />
        <Stack.Screen
          name="ProductView"
          component={ProductViewScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen name="Login" component={LoginScreen} />

        {/* Protected screens - require auth (screens will redirect to Login if needed) */}
        <Stack.Screen name="Home" component={HomeScreen} />
        <Stack.Screen name="DraftProducts" component={DraftProductsScreen} />
        <Stack.Screen name="Favorites" component={FavoritesScreen} />
        <Stack.Screen name="PurchaseHistory" component={PurchaseHistoryScreen} />
        <Stack.Screen name="ProfileSettings" component={ProfileSettingsScreen} />
        <Stack.Screen
          name="Camera"
          component={CameraScreen}
          options={{ presentation: 'fullScreenModal' }}
        />
        <Stack.Screen
          name="ProductDetail"
          component={ProductDetailScreen}
        />
        <Stack.Screen
          name="JoinMarket"
          component={JoinMarketScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="CreateMarket"
          component={CreateMarketScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketAdmin"
          component={MarketAdminScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketSellers"
          component={MarketSellersScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketSpaces"
          component={MarketSpacesScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketSettings"
          component={MarketSettingsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketPayouts"
          component={MarketPayoutsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketPOS"
          component={MarketPOSScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketPrintTags"
          component={MarketPrintTagsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="MarketRefunds"
          component={MarketRefundsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="SpaceReservations"
          component={SpaceReservationsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="SpaceBrowse"
          component={SpaceBrowseScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="FloorPlanEditor"
          component={FloorPlanEditorScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="VisualSpaceBrowse"
          component={VisualSpaceBrowseScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="BulkUpload"
          component={BulkUploadScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="ReservationSettings"
          component={ReservationSettingsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="PrintTags"
          component={PrintTagsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="StorePrintTags"
          component={StorePrintTagsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="BoothView"
          component={BoothViewScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="Checkout"
          component={CheckoutScreen}
          options={{ headerShown: false }}
        />
        {/* Store management screens */}
        <Stack.Screen
          name="MyStores"
          component={MyStoresScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="StoreInventory"
          component={StoreInventoryScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="StoreCamera"
          component={StoreCameraScreen}
          options={{ presentation: 'fullScreenModal' }}
        />
        <Stack.Screen
          name="StoreSettings"
          component={StoreSettingsScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="CreateStore"
          component={CreateStoreScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="SupplierManagement"
          component={SupplierManagementScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="InventoryReports"
          component={InventoryReportsScreen}
          options={{ headerShown: false }}
        />
        {/* Merchant screens */}
        <Stack.Screen name="MerchantStoreSelect" component={MerchantStoreSelectScreen} />
        <Stack.Screen name="MerchantDashboard" component={MerchantDashboardScreen} />
        <Stack.Screen name="MerchantPOS" component={MerchantPOSScreen} />
        <Stack.Screen name="MerchantActivity" component={MerchantActivityScreen} />
        <Stack.Screen name="MerchantMore" component={MerchantMoreScreen} />
        <Stack.Screen name="MerchantStaff" component={MerchantStaffScreen} />
        <Stack.Screen name="SocialAccounts" component={SocialAccountsScreen} />
        {/* Inventory CMS screens */}
        <Stack.Screen name="CategoryManagement" component={CategoryManagementScreen} />
        <Stack.Screen name="StockAdjustment" component={StockAdjustmentScreen} />
        <Stack.Screen name="PurchaseOrders" component={PurchaseOrdersScreen} />
        <Stack.Screen name="PurchaseOrderDetail" component={PurchaseOrderDetailScreen} />
        <Stack.Screen name="MerchantProductEdit" component={MerchantProductEditScreen} />
        {/* Offer screens */}
        <Stack.Screen name="SubmitOffer" component={SubmitOfferScreen} />
        <Stack.Screen name="MyOffers" component={MyOffersScreen} />
        <Stack.Screen name="IncomingOffers" component={IncomingOffersScreen} />
        <Stack.Screen name="StoreTransactions" component={StoreTransactionsScreen} options={{ headerShown: false }} />
      </Stack.Navigator>
      </NavigationShell>
    </View>
  )
}

const prefix = Linking.createURL('/')

const screens = {
  public: {
    Unsubscribe: 'unsubscribe',
    Landing: '',
    Login: 'login',
    BuyerHome: 'home',
    SearchScreen: 'search',
    Discoveries: 'discoveries',
    MapBrowse: 'map',
    StoreBrowse: 'store/:marketId',
    ProductView: 'product/:productId',
    BoothView: 'booth/:sellerId/:reservationId',
  },
  buyer: {
    BuyerProfile: 'profile',
    ProfileSettings: 'profile/settings',
    Favorites: 'favorites',
    PurchaseHistory: 'purchases',
    DraftProducts: 'drafts',
    SubmitOffer: 'offer/:storeId',
    MyOffers: 'offers',
  },
  seller: {
    Home: 'seller-home',
    Camera: 'camera',
    ProductDetail: 'store/product/:productId',
    JoinMarket: 'markets',
    CreateMarket: 'markets/create',
    BulkUpload: 'bulk-upload/:reservationId',
    ReservationSettings: 'reservation/:reservationId/settings',
    PrintTags: 'reservation/:reservationId/print',
    StorePrintTags: 'store/:storeId/print-tags',
    Checkout: 'reservation/:reservationId/checkout',
    MyStores: 'stores',
    CreateStore: 'stores/create',
    StoreInventory: 'store/:storeId',
    StoreCamera: 'store/:storeId/camera',
    StoreSettings: 'store/:storeId/settings',
    IncomingOffers: 'store/:storeId/offers',
    SupplierManagement: 'store/:storeId/suppliers',
    InventoryReports: 'store/:storeId/reports',
  },
  market: {
    MarketAdmin: 'market/:marketId',
    MarketSellers: 'market/:marketId/sellers',
    MarketSpaces: 'market/:marketId/spaces',
    MarketSettings: 'market/:marketId/settings',
    MarketPayouts: 'market/:marketId/payouts',
    MarketPOS: 'market/:marketId/pos',
    MarketPrintTags: 'market/:marketId/print-tags',
    MarketRefunds: 'market/:marketId/refunds',
    SpaceReservations: 'market/:marketId/reservations',
    SpaceBrowse: 'market/:marketId/browse',
    FloorPlanEditor: 'market/:marketId/floor-plan',
    VisualSpaceBrowse: 'market/:marketId/visual-browse',
  },
  merchant: {
    MerchantStoreSelect: 'merchant',
    MerchantDashboard: 'merchant/:storeId',
    MerchantPOS: 'merchant/:storeId/pos',
    MerchantActivity: 'merchant/:storeId/activity',
    MerchantMore: 'merchant/:storeId/more',
    MerchantStaff: 'merchant/:storeId/staff',
    SocialAccounts: 'merchant/:storeId/social',
    CategoryManagement: 'merchant/:storeId/categories',
    StockAdjustment: 'merchant/:storeId/stock',
    PurchaseOrders: 'merchant/:storeId/orders',
    PurchaseOrderDetail: 'merchant/:storeId/orders/:orderId',
    MerchantProductEdit: 'merchant/:storeId/product/:productId',
    StoreTransactions: 'merchant/:storeId/transactions',
  },
}

const linking = {
  prefixes: [prefix, 'https://fliisi.vercel.app', 'https://kirpputori.dev.sakualy.ai', 'http://localhost:3009', 'http://localhost:8081'],
  config: {
    screens: {
      ...screens.public,
      ...screens.buyer,
      ...screens.seller,
      ...screens.market,
      ...screens.merchant,
    },
  },
}

function AppContent() {
  return (
    <View style={styles.appContainer}>
      <AppNavigator />
      <StatusBar style="auto" />
    </View>
  )
}

function AppWithLanguage() {
  const { setRouteName, setRouteParams } = useNavShell()
  const { user } = useAuth()

  const handleStateChange = () => {
    const route = navigationRef.current?.getCurrentRoute()
    if (route) {
      setRouteName(route.name)
      setRouteParams((route.params as Record<string, unknown>) ?? {})
    }
  }

  const loadLanguage = async () => {
    const stored = await AsyncStorage.getItem('language')
    if (stored === 'fi' || stored === 'en') return stored
    if (user?.id) {
      const { data } = await supabase.from('users').select('preferred_language').eq('id', user.id).single()
      if (data?.preferred_language === 'fi' || data?.preferred_language === 'en') return data.preferred_language
    }
    return null
  }

  const saveLanguage = async (lang: 'fi' | 'en') => {
    await AsyncStorage.setItem('language', lang)
    if (user?.id) {
      await supabase.from('users').update({ preferred_language: lang }).eq('id', user.id)
    }
  }

  return (
    <I18nProvider loadLanguage={loadLanguage} saveLanguage={saveLanguage}>
      <ToastProvider>
        <NavigationContainer
          ref={navigationRef}
          onReady={() => routingInstrumentation?.registerNavigationContainer(navigationRef)}
          onStateChange={handleStateChange}
          linking={Platform.OS === 'web' ? linking : undefined}
          documentTitle={{
            formatter: () => 'Fliisi',
          }}
        >
          <AppContent />
        </NavigationContainer>
      </ToastProvider>
    </I18nProvider>
  )
}

const styles = StyleSheet.create({
  appContainer: {
    flex: 1,
  },
})

function App() {
  const [fontsLoaded] = useFonts({
    PlayfairDisplay_700Bold_Italic,
  })

  return (
    <NavShellProvider>
      <TamaguiProvider config={config} defaultTheme="garden">
        {!fontsLoaded ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator size="large" color="#748C70" />
          </View>
        ) : (
          <AuthProvider>
            <AppWithLanguage />
          </AuthProvider>
        )}
      </TamaguiProvider>
    </NavShellProvider>
  )
}

export default wrapWithSentry(App)
